/**
 * "Preview as learner" (contract §9.1): the learner's own shapes (§5.4 `MemberPathwayHome`
 * and `MemberStepScreen`) built from the practitioner's stored `AdminPathway`, so the
 * admin preview renders the REAL learner views (public/pathways/PathwayHomeView.jsx and
 * StepScreenView.jsx) instead of an imitation that would drift from them.
 *
 * A preview is a learner who can see everything and has done nothing:
 *   - every level and step is `available`, no step is `current`, and no `lock` is set;
 *   - progress is 0 of the pathway's mandatory steps (every step in `successive`);
 *   - a quiz has no attempt; the certificate, when enabled, is not yet issued;
 *   - weekly prompts are shown newest first, and only for a cohort pathway (as §5.4 does);
 *   - the phase-3 kinds (contract §10.7, §10.11) are built with nothing done: a practice
 *     step has no submission, a cohort live step is not attended, a self-paced one has no
 *     upcoming or completed booking, and a course step has 0 lessons done.
 *
 * A self-paced live step's `service` is built from the AdminPathway's resolution
 * (`live.service`, contract §10.6), which states no price: `price_cents` and `currency`
 * are carried when the resolution has them and are null otherwise.
 *
 * MEDIA. A learner's file urls point at the gated learner endpoint; the practitioner is not
 * enrolled, so the preview uses the admin url every MediaRef carries: the authenticated
 * `/api/admin/media/<media_id>/file` (pathway media are private assets, which the public
 * `/api/media/file/<id>` refuses). An upload known only by its `media_id` gets the url on
 * the same pattern. These are ABSOLUTE API paths (they already hold "/api"); the learner
 * views resolve them like their own (learnerApi.resolveApiUrl: the "/api" is the base's,
 * and the `/api/t/{slug}` prefix is kept on the platform host), and the session cookie
 * authenticates the `<video src>` / `<a href>`.
 *
 * Pure: no React, no transport.
 */

const mediaFileUrl = (mediaId) => `/api/admin/media/${encodeURIComponent(mediaId)}/file`;

const orderedSteps = (p) => (p.levels || []).flatMap((l, li) => (l.steps || []).map((s, si) => ({
  step: s, level: l, levelIndex: li + 1, position: si + 1, total: (l.steps || []).length,
})));

/** The mandatory flag a learner sees: every step of a successive pathway is mandatory. */
const isRequired = (p, s) => p.path_type === "successive" || s.required !== false;

const stepRow = (p, s) => ({
  id: s.id,
  kind: s.kind,
  title: s.title,
  required: isRequired(p, s),
  duration_minutes: Number.isFinite(s.duration_minutes) ? s.duration_minutes : 0,
  state: "available",
  current: false,
  lock: null,
  completed_at: null,
});

const newestFirst = (a, b) => String(b.created_at || "").localeCompare(String(a.created_at || ""));

/**
 * @param {object} p AdminPathway
 * @returns {object} MemberPathwayHome
 */
export function toPreviewHome(p) {
  const steps = orderedSteps(p);
  const requiredTotal = steps.filter(({ step }) => isRequired(p, step)).length;
  const cohort = p.schedule === "cohort";
  return {
    // MemberPathwaySummary
    id: p.id,
    title: p.title,
    status: p.status,
    path_type: p.path_type,
    enrolled_at: null,
    completed_at: null,
    percent: 0,
    required_total: requiredTotal,
    required_completed: 0,
    current_step: null,
    // MemberPathwayHome
    description: p.description || "",
    objectives: [...(p.objectives || [])],
    schedule: p.schedule,
    start_date: p.start_date || null,
    end_date: p.end_date || null,
    levels: (p.levels || []).map((l, i) => ({
      id: l.id,
      title: l.title,
      index: i + 1,
      state: "available",
      locked_by: null,
      steps: (l.steps || []).map((s) => stepRow(p, s)),
    })),
    prompts: cohort
      ? [...(p.thread || [])].sort(newestFirst).map((m) => ({ id: m.id, week: m.week, text: m.text, created_at: m.created_at }))
      : [],
    certificate: p.certificate_enabled ? { available: false } : null,
  };
}

const videoScreen = (v) => {
  if (!v) return null;
  if (v.source === "link") return { source: "link", url: v.url };
  return { source: "upload", url: v.url || mediaFileUrl(v.media_id), content_type: v.content_type || "" };
};

const fileScreen = (m) => ({
  url: m.url || mediaFileUrl(m.media_id),
  filename: m.filename,
  size: m.size,
  content_type: m.content_type,
});

const pdfScreen = (m) => {
  const { url, filename, size } = fileScreen(m);
  return { url, filename, size };
};

const enc = encodeURIComponent;

const practiceScreen = (s) => ({
  instructions: s.practice?.instructions || "",
  submission_required: Boolean(s.practice?.submission_required),
  submissions: [],
});

const serviceScreen = (svc) => (svc
  ? {
    id: svc.id,
    name: svc.name,
    duration_min: svc.duration_min,
    price_cents: Number.isFinite(svc.price_cents) ? svc.price_cents : null,
    currency: svc.currency || null,
    active: Boolean(svc.active),
  }
  : null);

const liveScreen = (p, s) => {
  const l = s.live || {};
  if (p.schedule === "cohort") {
    return {
      mode: "cohort",
      starts_at: l.starts_at || null,
      duration_minutes: Number.isFinite(s.duration_minutes) ? s.duration_minutes : 0,
      location_note: l.location_note || "",
      calendar_url: `/client/pathways/${enc(p.id)}/steps/${enc(s.id)}/calendar.ics`,
      attended: false,
    };
  }
  return {
    mode: "self_paced",
    location_note: l.location_note || "",
    service: serviceScreen(l.service),
    booking_path: l.service_id ? `/reserver?service=${enc(l.service_id)}` : "/reserver",
    upcoming: null,
    completed_booking: null,
  };
};

const courseScreen = (p, s) => {
  const courseId = s.course?.course_id || "";
  const info = s.course?.info || null;
  return {
    id: courseId,
    title: info?.title || "",
    cover_url: info?.cover_url || "",
    lessons_total: Number.isFinite(info?.lessons_count) ? info.lessons_count : 0,
    lessons_done: 0,
    published: Boolean(info?.published),
    player_path: `/learn/${enc(courseId)}?pathway=${enc(p.id)}&step=${enc(s.id)}`,
  };
};

/**
 * @param {object} p AdminPathway
 * @param {string} stepId
 * @returns {object|null} MemberStepScreen, or null when the pathway has no such step
 */
export function toPreviewStepScreen(p, stepId) {
  const steps = orderedSteps(p);
  const at = steps.findIndex(({ step }) => step.id === stepId);
  if (at < 0) return null;
  const { step: s, level, levelIndex, position, total } = steps[at];
  const next = steps[at + 1] ? steps[at + 1].step : null;
  return {
    pathway_id: p.id,
    pathway_title: p.title,
    path_type: p.path_type,
    level: { id: level.id, title: level.title, index: levelIndex },
    position: { index: position, total },
    step: stepRow(p, s),
    video: s.kind === "video" ? videoScreen(s.video) : null,
    reading: s.kind === "reading" && s.reading
      ? { body: s.reading.body || "", pdf: s.reading.pdf ? pdfScreen(s.reading.pdf) : null }
      : null,
    quiz: s.kind === "quiz" && s.quiz
      ? {
        pass_threshold: s.quiz.pass_threshold,
        // The answer key never reaches a learner view, the preview included.
        questions: (s.quiz.questions || []).map((q) => ({ id: q.id, question: q.question, choices: [...q.choices] })),
        attempts: 0,
        best_score: null,
        passed: false,
      }
      : null,
    practice: s.kind === "practice" ? practiceScreen(s) : null,
    live: s.kind === "live" ? liveScreen(p, s) : null,
    course: s.kind === "course" ? courseScreen(p, s) : null,
    resources: (s.resources || []).map(fileScreen),
    next: next ? { id: next.id, title: next.title, state: "available" } : null,
  };
}
